// «مكافأة بنك الأرقام» في شاشة «عمولتي» (0107) — ما يستحقّه البياع على حجوزاته من أرقام البنك.
//
// قرار المالك: «اللي هينفّذ حجز من تسجيل متابعات لأرقام دي هياخد على كل حجز 200 جنيه
// للحجوزات اللي تحت 10 آلاف، و300 للي فوق الـ10 آلاف — زيادة على عمولته الطبيعية».
//
// بندٌ خارجٌ عن العمولة يُضاف إليها (كمكافأة تقييم Google Map)، فله بطاقته. ويقول
// للبياع بلا لفّ: كم حجزًا من البنك هذا الشهر، وبكم، وعلى أي حجوزات — والقاعدة بأرقامها
// من الخادم لا مكتوبةً في نصّ، فلو غيّرها المالك تغيّر ما يقرؤه البياع معها.
import { useEffect, useState } from 'react';
import { ApiError, get, qs, type MyLeadBankBonus } from '../lib/api.ts';
import { Alert, Card, Empty, Spinner } from '../components/ui.tsx';
import { EGP, egpNum, shortDate } from '../lib/format.ts';

const TITLE = 'مكافأة بنك الأرقام';

/**
 * **القاعدة لافتةٌ لا هامش** (قرار المالك: «عاوز الكلام ده واضح ليهم علشان يهتموا بيه»):
 * المبلغان أكبرُ ما فيها على خلفيةٍ ملوّنة، والشرط في سطرٍ واحد. ثم خُفّضت درجتين
 * («أصغّر دول شوية، ظاهرين أوي» 21/09/2026): تُقرأ بلمحة ولا تسبق ما فوقها في النظر.
 * والأرقام من سياسة الخادم، فلو غيّرها المالك تغيّرت اللافتة معها.
 */
export function BonusRule({ policy: p, compact = false }: { policy: { threshold: string; underAmount: string; overAmount: string }; compact?: boolean }) {
  return (
    <div className={`${compact ? 'mt-0 mb-3' : 'mt-3'} rounded-xl border border-amber-300 bg-amber-50/70 p-3 text-amber-950`}>
      <div className="text-[0.9375rem] font-bold leading-snug">🎁 مكافأة على كل حجز تعمله من أرقام بنك الأرقام — زيادة على عمولتك الطبيعية</div>
      <div className="mt-2 flex flex-wrap gap-2">
        <div className="flex-1 rounded-lg border border-amber-200/70 bg-white/80 px-3 py-2 text-center">
          <div className="text-xl font-bold tabular-nums text-emerald-800">{egpNum(p.underAmount)} <span className="text-xs font-bold">{EGP}</span></div>
          <div className="mt-0.5 text-xs font-medium">لو سعر البيع أقل من {egpNum(p.threshold)} {EGP}</div>
        </div>
        <div className="flex-1 rounded-lg border border-amber-200/70 bg-white/80 px-3 py-2 text-center">
          <div className="text-xl font-bold tabular-nums text-emerald-800">{egpNum(p.overAmount)} <span className="text-xs font-bold">{EGP}</span></div>
          <div className="mt-0.5 text-xs font-medium">لو سعر البيع {egpNum(p.threshold)} {EGP} فأكثر</div>
        </div>
      </div>
      <div className="mt-2 text-xs leading-relaxed">
        الشرط: الرقم يكون <b>اتسحب من بنكك</b> واتسجّلت بيه متابعة، والحجز يتعمل <b>من المتابعة دي</b> («إنشاء حجز» من المتابعة) —
        وتتحسب لك في شهر الحجز وتنزل في كشف مرتبك.
      </div>
    </div>
  );
}

export function LeadBankBonusCard({ month }: { month: string }) {
  const [data, setData] = useState<MyLeadBankBonus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setData(null); setError(null);
    void (async () => {
      try { const r = await get<MyLeadBankBonus>(`/me/lead-bank-bonus${qs({ month })}`); if (alive) setData(r); }
      catch (e) { if (alive) setError(e instanceof ApiError ? e.message : 'تعذّر تحميل مكافأة بنك الأرقام'); }
    })();
    return () => { alive = false; };
  }, [month]);

  if (error) return <Card title={TITLE}><Alert>{error}</Alert></Card>;
  if (!data) return <Card title={TITLE}><Spinner label="جارٍ التحميل…" /></Card>;

  const earned = Number(data.amount) > 0;
  const p = data.policy;

  return (
    <Card title={TITLE}>
      <div className={`rounded-xl border p-4 ${earned ? 'border-emerald-200 bg-emerald-50/60' : 'border-slate-200 bg-slate-50'}`}>
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-sm text-ink-700">حجوزات من بنك الأرقام هذا الشهر:</span>
          <span className="text-2xl font-bold tabular-nums text-ink-900">{data.bookingsCount}</span>
          {earned && (
            <>
              <span className="text-sm text-ink-700">=</span>
              <span className="text-2xl font-bold tabular-nums text-emerald-900">{egpNum(data.amount)} {EGP}</span>
            </>
          )}
        </div>
        {earned && p && (
          <p className="mt-2 text-sm text-ink-700">
            {data.underCount} حجز تحت {egpNum(p.threshold)} {EGP} × {egpNum(p.underAmount)}
            {' + '}{data.overCount} حجز من {egpNum(p.threshold)} فأكثر × {egpNum(p.overAmount)}.
          </p>
        )}
      </div>

      {p ? <BonusRule policy={p} /> : <p className="mt-3 text-xs text-ink-500">لا سياسة مكافأة سارية على هذا الشهر.</p>}

      {data.bookings.length === 0 ? (
        <div className="mt-3"><Empty>لا حجوزات من بنك الأرقام هذا الشهر.</Empty></div>
      ) : (
        <div className="mt-3">
          <div className="overflow-x-auto">
            <table className="max-lg:min-w-[24rem] w-full table-fixed text-sm">
              <colgroup><col className="w-[25%]" /><col className="w-[35%]" /><col className="w-[20%]" /><col className="w-[20%]" /></colgroup>
              <thead>
                <tr className="border-b-2 border-slate-200 text-xs text-ink-600">
                  <th className="py-2 text-start">رقم الحجز</th>
                  <th className="py-2 text-start">العميل</th>
                  <th className="py-2 text-start">سعر البيع</th>
                  <th className="py-2 text-start">المكافأة</th>
                </tr>
              </thead>
              <tbody>
                {data.bookings.map(b => (
                  <tr key={b.bookingId} className="border-b border-slate-100">
                    <td className="py-2 font-mono text-xs">{b.bookingNo}<div className="text-[10px] text-ink-500">{shortDate(b.businessDate)}</div></td>
                    <td className="py-2 truncate">{b.customerName}</td>
                    <td className="py-2 tabular-nums">{egpNum(b.salePrice)}</td>
                    <td className="py-2 tabular-nums font-semibold text-emerald-800">+{egpNum(b.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}
